import React, { useState, useEffect, useRef } from 'react';
import { 
  FiEdit3, 
  FiTrash2, 
  FiEye,
  FiEyeOff,
  FiTag,
  FiMapPin
} from 'react-icons/fi';
import { Zone as ZoneType } from '../../shared/types/zone';
import './Zone.css';

interface ZoneProps {
  zone: ZoneType;
  isSelected: boolean;
  zoomLevel: number;
  panPosition: { x: number; y: number };
  rotation: number;
  imageRef: React.RefObject<HTMLImageElement | null>;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleVisibility: () => void;
  isVisible?: boolean;
}

const Zone: React.FC<ZoneProps> = ({
  zone,
  isSelected,
  zoomLevel,
  panPosition,
  rotation,
  imageRef,
  onClick,
  onEdit,
  onDelete,
  onToggleVisibility,
  isVisible = true
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [polygonPoints, setPolygonPoints] = useState<string>('');
  const [zoneCenter, setZoneCenter] = useState({ x: 0, y: 0 });
  const zoneRef = useRef<SVGPolygonElement>(null);

  // Calculate polygon points and center based on zone coordinates
  useEffect(() => {
    if (!imageRef.current || !zone.coordinates.length) return;

    const imageRect = imageRef.current.getBoundingClientRect();
    const containerRect = imageRef.current.parentElement?.getBoundingClientRect();

    if (!containerRect) return;

    // Get the natural image dimensions (before zoom)
    const naturalWidth = imageRef.current.naturalWidth;
    const naturalHeight = imageRef.current.naturalHeight;
    
    // Calculate the actual image dimensions on screen (after zoom)
    // const displayWidth = imageRect.width;
    // const displayHeight = imageRect.height;

    // Get the image's position relative to the container
    const imageOffsetX = imageRect.left - containerRect.left;
    const imageOffsetY = imageRect.top - containerRect.top;

    // Convert normalized coordinates to pixel positions, accounting for zoom
    const points = zone.coordinates.map(coord => {
      // Convert normalized coordinates to natural image coordinates
      const naturalX = coord.x * naturalWidth;
      const naturalY = coord.y * naturalHeight;
      
      // Scale by zoom level and position relative to container
      const x = imageOffsetX + (naturalX * zoomLevel);
      const y = imageOffsetY + (naturalY * zoomLevel);
      return `${x},${y}`;
    }).join(' ');

    setPolygonPoints(points);

    // Calculate center point for label positioning
    const centerX = zone.coordinates.reduce((sum, coord) => sum + coord.x, 0) / zone.coordinates.length;
    const centerY = zone.coordinates.reduce((sum, coord) => sum + coord.y, 0) / zone.coordinates.length;
    
    // Convert center to natural coordinates and scale by zoom
    const naturalCenterX = centerX * naturalWidth;
    const naturalCenterY = centerY * naturalHeight;
    
    setZoneCenter({
      x: imageOffsetX + (naturalCenterX * zoomLevel),
      y: imageOffsetY + (naturalCenterY * zoomLevel)
    });
  }, [zone.coordinates, zoomLevel, panPosition, rotation, imageRef]);

  // Handle zone click
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClick();
  };

  // Handle action buttons
  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEdit();
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm('Are you sure you want to delete this zone?')) {
      onDelete();
    }
  };

  const handleToggleVisibility = (event: React.MouseEvent) => {
    event.stopPropagation();
    onToggleVisibility();
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Zone Polygon */}
      <svg
        className="zone-svg"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: isSelected ? 100 : 50
        }}
      >
        <polygon
          ref={zoneRef}
          points={polygonPoints}
          fill={zone.color}
          fillOpacity={isSelected ? 0.3 : 0.2}
          stroke={zone.color}
          strokeWidth={isSelected ? 3 : 2}
          strokeOpacity={isSelected ? 0.8 : 0.6}
          className={`zone-polygon ${isSelected ? 'selected' : ''}`}
          style={{
            pointerEvents: 'all',
            cursor: 'pointer',
            transform: `rotate(${-rotation}deg)`,
            transformOrigin: `${zoneCenter.x}px ${zoneCenter.y}px`
          }}
          onClick={handleClick}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        />
      </svg>

      {/* Zone Label */}
      <div
        className={`zone-label ${isSelected ? 'selected' : ''}`}
        style={{
          position: 'absolute',
          left: zoneCenter.x - 50, // Center the label
          top: zoneCenter.y - 15,
          zIndex: isSelected ? 1001 : 101,
          transform: `rotate(${-rotation}deg)`,
          transformOrigin: 'center'
        }}
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div 
          className="zone-label-content"
          style={{ backgroundColor: zone.color }}
        >
          <FiMapPin size={12} />
          <span>{zone.name}</span>
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && !isSelected && (
        <div
          className="zone-tooltip"
          style={{
            position: 'absolute',
            left: zoneCenter.x + 15,
            top: zoneCenter.y - 40,
            zIndex: 1001,
            transform: `rotate(${-rotation}deg)`
          }}
        >
          <div className="zone-tooltip-content">
            <h4 className="zone-tooltip-title">
              {zone.name}
            </h4>
            {zone.description && (
              <p className="zone-tooltip-description">
                {zone.description.length > 100 
                  ? `${zone.description.slice(0, 100)}...`
                  : zone.description
                }
              </p>
            )}
            {zone.category && (
              <div className="zone-tooltip-category">
                <FiTag size={12} />
                <span>{zone.category}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Zone Actions (when selected) */}
      {isSelected && (
        <div
          className="zone-actions"
          style={{
            position: 'absolute',
            left: zoneCenter.x + 20,
            top: zoneCenter.y - 50,
            zIndex: 1002,
            transform: `rotate(${-rotation}deg)`
          }}
        >
          <div className="zone-actions-content">
            <button
              className="zone-action-btn visibility-btn"
              onClick={handleToggleVisibility}
              title={isVisible ? "Hide zone" : "Show zone"}
            >
              {isVisible ? <FiEye size={14} /> : <FiEyeOff size={14} />}
            </button>
            
            <button
              className="zone-action-btn edit-btn"
              onClick={handleEdit}
              title="Edit zone"
            >
              <FiEdit3 size={14} />
            </button>
            <button
              className="zone-action-btn delete-btn"
              onClick={handleDelete}
              title="Delete zone"
            >
              <FiTrash2 size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Zone;
